'use client';

import { AppImageClient, AppImageData } from '@components/appImage';
import { Button, ButtonParams } from '@components/button';
import { Card, CardVariant } from '@components/card';
import { Sanitizer } from '@components/sanitizer';
import { ScrollToFormButton } from '@components/scrollToFormButton';
import { Typography } from '@components/typography';
import { classNames } from '@lib/classNames/classNames';
import { cn } from '@lib/utils/cn';

import { useUtmHref } from '@/shared/hooks';

export interface PartnerCardProps {
  description: React.ReactNode;
  descriptionId?: string;
  /**
   * Фон карточки: данные AppImage из `getAppImageData(path, { fill: true, sizes })`.
   * Рендерится как fill-картинка под контентом.
   */
  backgroundData?: AppImageData;
  /** Тот же sizes, что передавался в `getAppImageData`. */
  backgroundSizes?: string;
  backgroundAlt?: string;
  cardHeight?: number;
  cardMinHeight?: number;
  smoothScroll?: boolean;
  cardVariant?: CardVariant;
  button?: ButtonParams;
}

export const PartnerCard = ({
  description,
  descriptionId,
  cardHeight,
  cardMinHeight,
  backgroundData,
  backgroundSizes,
  backgroundAlt = '',
  smoothScroll,
  cardVariant = 'secondary',
  button,
}: PartnerCardProps) => {
  const buttonLinkWithUtm = useUtmHref(button?.href);

  const wrapperStyles: React.CSSProperties = {};

  if (cardMinHeight) {
    wrapperStyles.minHeight = cardMinHeight;
  }

  if (cardHeight) {
    wrapperStyles.height = cardHeight;
  }

  return (
    <div
      className="relative size-full pt-[142px] max-sm:pt-0"
      style={wrapperStyles}
    >
      {backgroundData && (
        // Аналог background-size: cover с background-position 0 0
        <AppImageClient
          {...backgroundData}
          fill
          sizes={backgroundSizes}
          alt={backgroundAlt}
          className="object-cover object-left-top"
        />
      )}
      <Card
        variant={cardVariant}
        className="relative flex flex-col justify-between p-7 pt-6 max-sm:px-5 max-sm:py-6"
      >
        <div
          className={classNames({
            cls: 'flex h-full justify-between max-sm:items-end',
          })}
        >
          <div className="flex flex-col gap-6 max-sm:gap-4">
            {/* as="div": description — произвольный ReactNode, внутрь может
                прийти блочный элемент, а <p> его содержать не может. */}
            <Typography variant="text-m" as="div" id={descriptionId}>
              {typeof description === 'string' ? (
                <Sanitizer as="span" htmlContent={description} />
              ) : (
                description
              )}
            </Typography>

            {buttonLinkWithUtm ? (
              smoothScroll ? (
                <ScrollToFormButton
                  variant={button?.variant ?? 'primary'}
                  className={cn(
                    'flex w-full items-center justify-center max-sm:w-full',
                    button?.className,
                  )}
                >
                  {button?.label}
                </ScrollToFormButton>
              ) : (
                <Button
                  className={cn(
                    'flex w-full items-center justify-center max-sm:w-full',
                    button?.className,
                  )}
                  href={buttonLinkWithUtm}
                  target={button?.target ?? '_blank'}
                  variant={button?.variant ?? 'primary'}
                >
                  {button?.label}
                </Button>
              )
            ) : null}
          </div>
        </div>
      </Card>
    </div>
  );
};
